import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell.js";

/** The two kinds of video, and where each one is made. */
export const PROJECT_KINDS = [
  {
    id: "demo",
    to: "/projects/new/demo",
    title: "Product demo",
    summary:
      "From a screen recording. Record a tab with the extension or upload a file; Veyso trims the dead air, zooms on clicks and writes the narration.",
    goodFor: ["Walkthroughs", "Features", "Sales demos", "Support answers"],
    needs: "a recording, or the Chrome extension",
  },
  {
    id: "launch",
    to: "/projects/new/launch",
    title: "Launch video",
    summary:
      "A motion graphic written from your website or a sentence about the product, with a voice-over. Ten styles, about ten minutes, nothing to film.",
    goodFor: ["Launches", "Announcements", "Social posts"],
    needs: "a website address or a sentence",
  },
] as const;

export type ProjectKind = (typeof PROJECT_KINDS)[number]["id"];

/**
 * The first question of a new project: which kind of video.
 *
 * The two share nothing — a product demo starts from a recording, a launch
 * video from a website or a sentence — so each has its own page, and this one
 * only sends you to the right one. The launch video used to sit as a card on
 * top of the recording page, where it read as part of that form.
 */
export const NewProjectChooser: React.FC = () => {
  const navigate = useNavigate();
  const [picked, setPicked] = useState<ProjectKind | null>(null);
  const chosen = PROJECT_KINDS.find((kind) => kind.id === picked);

  return (
    <AppShell breadcrumb={<span className="text-ink-400">New project</span>}>
      <div className="mx-auto flex max-w-[860px] flex-col gap-7">
        <div>
          <h1 className="text-[26px] font-semibold text-ink-100">What would you like to make?</h1>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-300">
            Pick one. You can always start the other kind from here later.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2" role="group" aria-label="Kind of video">
          {PROJECT_KINDS.map((kind) => {
            const selected = kind.id === picked;
            return (
              <button
                key={kind.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setPicked(kind.id)}
                // A second click on the chosen card goes straight in.
                onDoubleClick={() => navigate(kind.to)}
                className={`surface flex flex-col gap-4 p-[18px] text-left transition-colors ${
                  selected ? "border-ember-500 ring-4 ring-ember-500/15" : "hover:border-[var(--edge-strong)]"
                }`}
              >
                <KindPicture kind={kind.id} />
                <span className="flex flex-col gap-1.5">
                  <span className="text-[17px] font-semibold text-ink-100">{kind.title}</span>
                  <span className="text-[13px] leading-[1.55] text-ink-300">{kind.summary}</span>
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {kind.goodFor.map((use) => (
                    <span key={use} className="rounded-full bg-bay-900 px-2.5 py-1 text-[11.5px] text-ink-300">
                      {use}
                    </span>
                  ))}
                </span>
                <span className="text-[12px] text-ink-400">You need: {kind.needs}</span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-4">
          <Link to="/dashboard" className="btn btn-ghost">
            Cancel
          </Link>
          {chosen ? (
            <Link to={chosen.to} className="btn btn-primary">
              Continue to {chosen.title.toLowerCase()}
            </Link>
          ) : (
            <button type="button" className="btn btn-primary" disabled>
              Choose one to continue
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
};

/** A small drawing of each kind: a browser being recorded, or a title on a dark ground. */
const KindPicture: React.FC<{ kind: ProjectKind }> = ({ kind }) =>
  kind === "demo" ? (
    <span
      className="relative flex h-[168px] w-full items-center justify-center overflow-hidden rounded-[10px] bg-bay-900"
      aria-hidden="true"
    >
      <span className="flex h-[112px] w-[74%] flex-col overflow-hidden rounded-[8px] border border-[var(--edge)] bg-page">
        <span className="flex h-4 items-center gap-1 bg-bay-800 px-2">
          <span className="h-[5px] w-[5px] rounded-full bg-bay-600" />
          <span className="h-[5px] w-[5px] rounded-full bg-bay-600" />
          <span className="h-[5px] w-[5px] rounded-full bg-bay-600" />
        </span>
        <span className="flex flex-1 gap-2 p-2.5">
          <span className="w-[28%] rounded bg-bay-900" />
          <span className="flex flex-1 flex-col gap-1.5">
            <span className="h-2 w-[70%] rounded-sm bg-bay-750" />
            <span className="h-2 w-1/2 rounded-sm bg-bay-750" />
            <span className="mt-1.5 h-[26px] w-[40%] rounded-[5px] border-[1.5px] border-ember-500 bg-ember-500/12" />
          </span>
        </span>
      </span>
      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full border border-[var(--edge)] bg-page px-2 py-1 text-[11px] text-ink-300">
        <span className="h-[7px] w-[7px] rounded-full bg-signal-bad" />
        Recording
      </span>
    </span>
  ) : (
    <span
      className="flex h-[168px] w-full flex-col items-center justify-center gap-2.5 overflow-hidden rounded-[10px] bg-[#14161b]"
      aria-hidden="true"
    >
      <span className="text-[24px] font-bold tracking-[-0.02em] text-white">
        Get paid <span className="text-ember-500">on time.</span>
      </span>
      <span className="flex gap-1.5">
        <span className="h-1 w-9 rounded-sm bg-ember-500" />
        <span className="h-1 w-[18px] rounded-sm bg-[#3a3e47]" />
        <span className="h-1 w-[18px] rounded-sm bg-[#3a3e47]" />
      </span>
    </span>
  );
